/**
 * Domain detection and routing utilities for centralized login.
 * 
 * This module handles multi-domain authentication routing where:
 * - login.cnvrgnx.com is the central authentication portal
 * - platform.cnvrgnx.com is the platform admin workspace
 * - <tenant>.cnvrgnx.com are tenant-specific workspaces
 */

import { LOGIN_URL } from '@/config/auth';

export type DomainType = 'login' | 'platform' | 'tenant' | 'marketing' | 'development';

/**
 * Detect the current domain type based on hostname.
 */
export function getCurrentDomainType(): DomainType {
  const hostname = window.location.hostname;
  
  // Development environments
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  if (hostname.endsWith('.lovable.app') || hostname.endsWith('.lovableproject.com')) return 'development';
  
  // Production and environment-specific domains
  if (hostname === 'login.cnvrgnx.com' || 
      hostname === 'login-dev.cnvrgnx.com' || 
      hostname === 'login-preview.cnvrgnx.com') return 'login';
  if (hostname === 'platform.cnvrgnx.com' || 
      hostname === 'platform-dev.cnvrgnx.com' || 
      hostname === 'platform-preview.cnvrgnx.com') return 'platform';
  if (hostname === 'cnvrgnx.com' || hostname === 'www.cnvrgnx.com') return 'marketing';
  if (hostname.endsWith('.cnvrgnx.com')) return 'tenant';
  
  return 'development';
}

/**
 * Check if currently on the central login domain.
 */
export function isLoginDomain(): boolean {
  return getCurrentDomainType() === 'login';
}

/**
 * Check if currently on the platform admin domain.
 */
export function isPlatformDomain(): boolean {
  return getCurrentDomainType() === 'platform';
}

/**
 * Check if currently in a development environment.
 */
export function isDevelopment(): boolean {
  return getCurrentDomainType() === 'development';
}

/**
 * Check if currently on a tenant domain.
 */
export function isTenantDomain(): boolean {
  return getCurrentDomainType() === 'tenant';
}

/**
 * Get the tenant subdomain from the current hostname.
 * Returns null if not on a tenant domain.
 */
export function getTenantSubdomain(): string | null {
  const hostname = window.location.hostname;
  
  if (!hostname.endsWith('.cnvrgnx.com')) return null;
  
  const subdomain = hostname.replace('.cnvrgnx.com', '');
  
  // Exclude known system subdomains (centralized auth + platform infrastructure)
  const systemSubdomains = [
    'login', 'login-dev', 'login-preview',
    'platform', 'platform-dev', 'platform-preview',
    'auth', 'auth-dev', 'auth-preview',
    'scim', 'www', 'app', 'admin', 'api'
  ];
  if (systemSubdomains.includes(subdomain)) {
    return null;
  }
  
  return subdomain;
}

/**
 * Get the URL to redirect unauthenticated users to for login.
 * Includes the current URL as a redirect parameter.
 */
export function getLoginRedirectUrl(currentUrl?: string): string {
  const redirectTarget = currentUrl || window.location.href;
  const loginUrl = import.meta.env.VITE_LOGIN_URL || LOGIN_URL;
  return `${loginUrl}?redirect=${encodeURIComponent(redirectTarget)}`;
}

/**
 * Get the current domain's base URL for constructing redirect targets.
 */
export function getCurrentBaseUrl(): string {
  return `${window.location.protocol}//${window.location.host}`;
}

/**
 * Validate that a URL is a valid cnvrgnx.com subdomain for redirect purposes.
 */
export function isValidRedirectDomain(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname.endsWith('.cnvrgnx.com') && 
           !parsed.hostname.startsWith('login');
  } catch {
    return false;
  }
}

/**
 * Check if a URL is a valid workspace redirect target.
 * Valid targets are tenant subdomains or platform, excluding marketing and auth infrastructure.
 */
export function isValidWorkspaceRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.endsWith('.cnvrgnx.com')) return false;
    if (parsed.hostname === 'cnvrgnx.com') return false; // bare domain
    
    const subdomain = parsed.hostname.replace('.cnvrgnx.com', '');
    // Note: 'platform' is NOT in this list because it IS a valid workspace for platform admins
    const nonWorkspaceSubdomains = [
      'login', 'login-dev', 'login-preview',
      'www', 'auth', 'auth-dev', 'auth-preview',
      'scim', 'api', 'app', 'admin'
    ];
    
    return subdomain.length > 0 && !nonWorkspaceSubdomains.includes(subdomain);
  } catch {
    return false;
  }
}
